import { Mail, Phone, MapPin, Star, Globe, CalendarDays, Loader2 } from "lucide-react";
import { Button } from "@/design-system/components/ui/button";
import { cn } from "@/design-system/utils";
import { useTranslation } from "react-i18next";
import type { Clinic } from "@fysaro/domain";
import { useCreateBookingSession } from "./hooks/useCreateBookingSession";
import { HelpTrigger } from "@/shared/help/components/HelpTrigger";
import { redirectToApp } from "@/utils/appUrl";

export function PublicClinicHeader({ clinic }: { clinic: Clinic }) {
  const { t } = useTranslation(["clinic", "booking-patient"]);
  const createSession = useCreateBookingSession();

  const handleQuickBook = async () => {
    try {
      // Generamos la sesión puente en la BD de forma así­ncrona
      const response = await createSession.mutateAsync({
        clinicId: clinic.id,
        source: "DIRECT_LINK", // Indicamos que es un enlace directo sin servicio
      });

      // Viajamos limpios usando el sessionId recién creado
      const targetPath = `/patient/bookings/landing?sessionId=${response.id}`;
      redirectToApp(targetPath);
    } catch (error) {
      console.error("Error al crear la sesión puente de reserva", error);
    }
  };

  return (
    <div className="relative">
      <div className="overflow-hidden bg-card border rounded-2xl shadow-sm">
        {/* Banner adaptable con degradado */}
        <div className="h-24 md:h-32 w-full bg-gradient-to-r from-primary/20 to-primary/5" />

        <div className="px-4 md:px-8 pb-6 md:pb-8">
          {/* Bloque de Logo e Información básica */}
          <div className="relative flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-end -mt-16 md:-mt-12 text-center md:text-left">
            {/* Logo o Fallback */}
            <div className="h-28 w-28 md:h-32 md:w-32 rounded-2xl overflow-hidden border-4 border-card bg-muted shadow-xl shrink-0 flex items-center justify-center">
              {clinic?.imageUrl ? (
                <img
                  src={clinic.imageUrl}
                  alt={clinic.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Globe className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground/40" />
              )}
            </div>

            {/* Tí­tulo y Ciudad principal */}
            <div className="flex-1 space-y-2 md:space-y-1 mb-2 w-full">
              <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
                <h1 className="text-2xl md:text-3xl font-black tracking-tight text-foreground">
                  {clinic?.name}
                </h1>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {clinic?.isMain && (
                    <span className="bg-amber-500/10 text-amber-600 border border-amber-500/20 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                      <Star size={10} fill="currentColor" />{" "}
                      {t("clinic:details.main", "Sede Central")}
                    </span>
                  )}
                  <span
                    className={cn(
                      "px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border",
                      clinic?.status === "ACTIVE"
                        ? "bg-green-500/10 text-green-600 border-green-500/20"
                        : "bg-red-500/10 text-red-600 border-red-500/20",
                    )}
                  >
                    {clinic?.status === "ACTIVE"
                      ? t("clinic:details.open", "Abierto")
                      : t("clinic:details.closed", "Cerrado")}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-2 gap-y-1 text-muted-foreground font-medium text-xs md:text-sm">
                <MapPin size={14} className="text-primary shrink-0" />
                <span>{clinic?.city || t("clinic:details.no_location")}</span>
                {clinic?.zipCode && (
                  <>
                    <span className="text-muted-foreground/30 hidden sm:inline">
                      •
                    </span>
                    <span>{clinic?.zipCode}</span>
                  </>
                )}
              </div>
            </div>

            {/* CTA de Reserva Rápida integrado en la zona superior derecha en pantallas de escritorio */}
            <div className="flex w-full md:w-auto items-center justify-center md:justify-end gap-2 mt-2 md:mt-0 shrink-0">
              <HelpTrigger scope="PUBLIC" />
              <Button
                onClick={handleQuickBook}
                disabled={createSession.isPending}
                className="w-full md:w-auto rounded-xl shadow-md font-bold text-xs uppercase tracking-wider h-11 px-6"
              >
              {createSession.isPending ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <CalendarDays size={14} className="mr-2" />
              )}
              {t("booking-patient:actions.book", "Reservar Cita Online")}
              </Button>
            </div>
          </div>

          {/* Bloque inferior: Dirección Completa + Contacto */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-8 border-t pt-6 md:pt-8">
            {/* Dirección Postal y Botón de Maps */}
            <div className="md:col-span-2 space-y-2 text-left">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                {t("clinic:details.complete_address", "Dirección de la Sede")}
              </h3>
              <p className="text-foreground font-semibold leading-relaxed text-sm max-w-2xl">
                {clinic?.formattedAddress ||
                  clinic?.address ||
                  t("clinic:details.no_formatted_address")}
              </p>
              <p className="text-xs text-muted-foreground font-medium">
                {clinic?.city}
                {clinic?.zipCode ? `, ${clinic.zipCode}` : ""}
              </p>
            </div>

            {/* Datos Directos de Contacto */}
            <div className="space-y-3 bg-muted/30 p-4 rounded-2xl border border-dashed text-left">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                {t("clinic:details.contact", "Contacto Directo")}
              </h3>
              <div className="space-y-2.5 break-all">
                {/* Teléfono Clicable */}
                {clinic.phone && (
                  <a
                    href={`tel:${clinic.phone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition text-sm group"
                  >
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <Phone size={14} />
                    </div>
                    <div className="flex flex-col truncate">
                      <span className="font-semibold text-foreground group-hover:text-primary transition truncate">
                        {clinic.phone}
                      </span>
                    </div>
                  </a>
                )}

                {/* Email Clicable */}
                {clinic.email && (
                  <a
                    href={`mailto:${clinic.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition text-sm group"
                  >
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <Mail size={14} />
                    </div>
                    <div className="flex flex-col truncate">
                      <span className="font-semibold text-foreground group-hover:text-primary transition truncate">
                        {clinic.email}
                      </span>
                    </div>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
